import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { Application } from "@/types/startup";
import { ApplicationsList } from "@/components/startup/ApplicationsList";
import { OwnedStartups } from "@/components/startup/OwnedStartups";
import { ApplicationDialog } from "@/components/startup/ApplicationDialog";
import { StartupDashboardSkeleton } from "@/components/startup/StartupDashboardSkeleton";
import { useStartupApplications } from "@/hooks/useStartupApplications";

const StartupDashboard = () => {
  const { user } = useAuth();
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);

  const { data: startups, isLoading, refetch } = useQuery({
    queryKey: ['user-startups', user?.id],
    queryFn: async () => {
      if (!user) return [];
      console.log('Fetching startups for user:', user.id);
      const { data, error } = await supabase
        .from('startups')
        .select(`
          *,
          startup_roles (*),
          startup_applications (
            *,
            profiles (
              full_name,
              avatar_url
            ),
            startup_roles (
              title
            ),
            startups (
              name
            )
          )
        `)
        .eq('user_id', user.id);
      
      if (error) {
        console.error('Error fetching startups:', error);
        throw error;
      }
      console.log('Fetched startups:', data);
      return data;
    },
    enabled: !!user
  });

  const { applications, handleStatusUpdate } = useStartupApplications(startups);

  const handleDelete = async (id: string) => {
    console.log('Deleting startup:', id);
    await refetch();
  };

  if (isLoading) {
    return <StartupDashboardSkeleton />;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="container mx-auto py-8 px-4"
    >
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-4xl font-bold">
          <span className="text-white">My</span>{" "}
          <span className="text-flux-cyan">Startups</span>
        </h1>
        <Link to="/communities/startups/create">
          <Button className="bg-flux-cyan hover:bg-flux-cyan/80">
            <Plus className="w-4 h-4 mr-2" />
            Create New Startup
          </Button>
        </Link>
      </div>

      <ApplicationsList 
        applications={applications}
        onSelectApplication={setSelectedApplication}
      />

      <OwnedStartups 
        startups={startups || []} 
        onDelete={handleDelete}
      />

      <ApplicationDialog
        application={selectedApplication}
        onOpenChange={(open) => !open && setSelectedApplication(null)}
        onStatusUpdate={handleStatusUpdate}
      />
    </motion.div>
  );
};

export default StartupDashboard;